import { formatTimestampFull } from "@@common";
import { formatDate } from "../../common/formatDate";
import getCurrencyFormatter from "../../common/getCurrencyFormatter";
import { formatPhoneNumber } from "../Components/DataDisplay/phoneNumberFormatting";
import { PropertyDisplayFormat } from "../Models/PropertyDisplayFormat";
import { maskDisplayValue } from "./piiMasking";

export type DisplayValueFormatOptions = {
  format?: PropertyDisplayFormat;
  currencyCode?: string;
  phoneNumberFormat?: string;
};

export type DisplayValueMaskOptions = {
  maskPii?: boolean;
  fieldPath?: string;
  label?: string;
  observedPaths?: string[];
  format?: PropertyDisplayFormat;
};

export function isEmptyDate(value: unknown): boolean {
  if (!value) {
    return true;
  }

  const dateStr = typeof value === "string" ? value : value.toString();
  return dateStr.startsWith("0001-01-01");
}

export function formatRawDisplayValue(value: unknown, options: DisplayValueFormatOptions = {}): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.every((item) => item === null || item === undefined || typeof item !== "object")
        ? value.filter((item) => item !== null && item !== undefined).join(", ")
        : "";
    }

    return "";
  }

  switch (options.format) {
    case PropertyDisplayFormat.Date:
      return isEmptyDate(value) ? "" : formatDate(value as string | Date);
    case PropertyDisplayFormat.DateTime:
      return isEmptyDate(value) ? "" : formatTimestampFull(value as string);
    case PropertyDisplayFormat.Currency: {
      const code = options.currencyCode || "USD";
      const numericValue = typeof value === "number" ? value : parseFloat(value.toString());

      if (isNaN(numericValue)) {
        return value.toString();
      }

      const formatter = getCurrencyFormatter(code);
      return formatter.format(numericValue);
    }
    case PropertyDisplayFormat.Percent: {
      const numericValue = typeof value === "number" ? value : parseFloat(value.toString());
      if (isNaN(numericValue)) {
        return value.toString();
      }

      return `${parseFloat(numericValue.toPrecision(10))}%`;
    }
    case PropertyDisplayFormat.Number: {
      const numericValue = typeof value === "number" ? value : parseFloat(value.toString());
      if (isNaN(numericValue)) {
        return value.toString();
      }

      return numericValue.toLocaleString();
    }
    case PropertyDisplayFormat.PhoneLink:
      return formatPhoneNumber(value, options.phoneNumberFormat);
    default:
      return value.toString();
  }
}

export function formatDisplayValue(value: unknown, formatOptions: DisplayValueFormatOptions = {}, maskOptions: DisplayValueMaskOptions = {}): string {
  const formattedValue = formatRawDisplayValue(value, formatOptions);

  return maskDisplayValue(formattedValue, {
    enabled: maskOptions.maskPii ?? false,
    fieldPath: maskOptions.fieldPath,
    label: maskOptions.label,
    observedPaths: maskOptions.observedPaths,
    format: maskOptions.format ?? formatOptions.format,
  });
}
